import type { ReactElement, ReactNode, RefObject } from 'react';
import { Icon } from '../Icon';
import type { Action } from './Actions';
import {
  Button,
  MenuItem,
  MenuList,
  MenuListProps,
  MenuSeparator,
  Popover,
  PopoverRef,
  Size,
  ButtonProps,
} from '@ff-it/ui';

import { Placement } from '@floating-ui/react';

type ActionDropdownProps = {
  actions: (Action | '-')[];
  children?: ReactNode;
  testId?: string;
  menuTestId?: string;
  className?: string;
  size?: Size | null;
  buttonRef?: RefObject<HTMLButtonElement>;
  portalId?: string;
  menuSize?: MenuListProps['size'];
  after?: ReactNode;
  popoverRef?: RefObject<PopoverRef>;
} & Pick<ButtonProps, 'variant' | 'icon' | 'size'>;

export const allowedPlacements: Placement[] = ['bottom-start', 'bottom-end', 'top-start', 'top-end'];

export function ActionDropdown({
  actions,
  children,
  icon,
  testId,
  className,
  variant,
  size,
  menuSize,
  buttonRef,
  after,
  popoverRef,
}: ActionDropdownProps): ReactElement | null {
  if (actions.length === 0) {
    return null;
  }

  return (
    <Popover
      closeOnContentClick
      ref={popoverRef}
      interaction="click"
      placement="auto"
      placementOptions={{ allowedPlacements }}
      content={
        <MenuList testId={testId ? `${testId}-menu` : undefined} size={menuSize}>
          {actions.map((item, idx) => {
            if (item === '-') {
              return <MenuSeparator key={`separator-${idx}`} />;
            }

            const { action, button: { icon, className: _, children, variant: _variant, ...button } = {} } = item;

            return (
              <MenuItem
                key={action}
                testId={`action-${action}`}
                {...(button as any)}
                prefix={icon ? <Icon icon={icon} /> : undefined}
                label={children}
              />
            );
          })}
          {after}
        </MenuList>
      }
    >
      <Button testId={testId} className={className} variant={variant} size={size} ref={buttonRef} icon={icon}>
        {children}
      </Button>
    </Popover>
  );
}
